/**
 * Los documentos del caso agrupados por REQUISITO, con lo que decidió la IA y
 * lo que decidió el ejecutivo. Lo leen la pestaña Documentos y la Ficha, así
 * que las dos cuentan lo mismo.
 *
 * POR QUÉ EXISTE
 * --------------
 * 19-oct-2026 (diseño «Panel de documentos», propuestas 1a y 1b): Leslie
 * preguntaba «¿cuáles están validados para descargarlos?» mirando una lista de
 * ARCHIVOS. La pregunta es por requisito.
 *
 * 20-oct-2026 (Diego Casallo): que el ejecutivo pueda aprobar o rechazar los
 * documentos, y que lo que él valide diga que lo validó él. Lo que la IA
 * validó SIGUE validado —Ruben: dejarlo «por validar» era un falso positivo—;
 * el ejecutivo decide sobre lo que la IA OBSERVÓ («Validar igual» o
 * «Rechazar», definitivas y con confirmación). Los estados:
 *
 *   ○ sin_recibir   el cliente no lo envió
 *   ⚠ observado     la IA lo objetó; el ejecutivo lo valida igual o lo rechaza
 *   ✓ validado      por la IA (el requisito quedó recibido) o por una persona
 *                   (APPROVED): se descarga
 *   ✕ rechazado     lo rechazó una persona
 *   ↻ pedido        se le pidió de nuevo; se espera el reenvío
 *
 * De qué requisito es cada archivo lo dice el backend, nunca el tipo: el que
 * lo cumplió por `documento_id`; los que vinieron a cubrirlo y el agente
 * observó, por `veredicto_agente.requisitos`. Lo que no quedó atado a ninguno
 * va a «Otros archivos».
 *
 * Puro y sin imports de paquetes: lo corre
 * `tests/unit/test_documentos_por_requisito.py` en Node con lo que devuelve el
 * portal de verdad.
 */
import { diaEnLima, fechaHoraEnLima, separarRechazados } from './documentos'
import { lecturaDelVeredicto, type DocConVeredicto } from './veredicto'
import {
  PEDIDO_DE_NUEVO, RECHAZADO_POR_ASESOR, marcaDelRequisito, type RequisitoRevisado,
} from './revision'

export type EstadoDelRequisito =
  | 'sin_recibir' | 'observado' | 'validado' | 'rechazado' | 'pedido' | 'no_aplica'
export type FiltroDeRequisitos = 'todos' | 'observado' | 'validado' | 'rechazado' | 'sin_recibir'

/** Lo que este módulo lee de un documento: un subconjunto de `ConversationDocument`. */
export interface DocDelCaso extends DocConVeredicto {
  doc_id: string
  filename: string
  doc_type?: string
  type?: string
  created_at: string
  motivo_de_la_persona?: string
}

/** Lo que este módulo lee de un requisito: un subconjunto de `ProgresoDocumental`. */
export interface RequisitoDelCaso extends RequisitoRevisado {
  doc_type?: string
  estado?: string
  observacion?: string
  /** El caso con que la IA lo observó: preselecciona el motivo al rechazar. */
  ultimo_case_id?: string
  motivo_revision?: string
  motivo_no_aplica?: string
  intentos?: number
  intentos_previos?: number
  archivo?: string
  /** DEF-111: el DNI cumplido con dos fotos, una por cara. La otra mitad. */
  documento_otra_cara?: string
}

export interface ArchivoDelRequisito<D> {
  doc: D
  /** El intento de §9.27 que registró el agente o, sin él, el orden de llegada. */
  n: number
  /** «DNI no corresponde al solicitante», corto, para la fila. */
  motivo: string
  hora: string
}

export interface GrupoDelRequisito<D> {
  /** Índice del requisito: dos requisitos pueden compartir tipo. */
  clave: string
  label: string
  estado: EstadoDelRequisito
  requisito: RequisitoDelCaso
  /** El archivo con el que se cumplió (validado), o null. */
  archivo: D | null
  /** Validado con dos fotos de un DNI, una por cara: la que llegó primero. Se
   *  muestra y se descarga junto con `archivo` (DEF-111). */
  otraCara: D | null
  /** Validado: quién lo validó. '' si no está validado. */
  validadoPor: 'ia' | 'persona' | ''
  /** El archivo sobre el que decide el ejecutivo (lo observado), o null. */
  aDecidir: D | null
  /** La línea de debajo del requisito: «COPIA_LITERAL.pdf · validado por la IA · 17:42». */
  sub: string
  /** La hora que importa en cada estado: la de la decisión, la del último
   *  intento, la del pedido. «17:58» si es de hoy. */
  hora: string
  /** Observado: lo que dijo la IA. */
  observacionIA: string
  /** Las alertas guardadas bajo el archivo que lo cumple. */
  observaciones: string[]
  /** El chip «3 intentos», o '' si no hay número que dar. */
  intentosTexto: string
  /** Observado: los intentos, el más nuevo primero. Si no: los anteriores. */
  archivos: Array<ArchivoDelRequisito<D>>
  /** Lo último que se le dijo al cliente por un archivo de este requisito. */
  mensaje: { texto: string; hora: string } | null
  /** El motivo que se preselecciona al rechazar (`MOTIVOS_DE_RECHAZO`), o ''. */
  motivoSugerido: string
  /** Rechazado: el archivo que rechazó la persona (sobre él se pide de nuevo). */
  rechazado: D | null
  /** Rechazado: ¿el cliente ya sabe que tiene que reenviarlo? Sí cuando la IA
   *  ya se lo pidió por ese archivo; no cuando la IA lo había dado por bueno. */
  clienteAvisado: boolean
  /**
   * Observado y sin intentos registrados: los archivos de ese TIPO que no
   * quedaron atados a ningún requisito. Son de antes de que se registrara el
   * veredicto por archivo (el caso de Flor, 19-oct-2026): no se sabe cuál
   * observó la IA, así que no se le atribuye ninguno — se OFRECEN para que el
   * ejecutivo elija cuál validar o rechazar. La conversación decide a qué
   * requisito va con su propia regla (`teleagent.revision`).
   */
  candidatos: D[]
}

export interface DocumentosPorRequisito<D> {
  grupos: Array<GrupoDelRequisito<D>>
  validados: number
  /** Lo observado por la IA: lo que espera la decisión del ejecutivo. */
  observados: number
  /** Rechazados por una persona, avisados o no. */
  rechazados: number
  sinRecibir: number
  /** Los requisitos que cuentan: sin los que no aplican (DEF-040). */
  aplicables: number
  /** Los archivos que no quedaron atados a ningún requisito. */
  otros: D[]
  /**
   * Lo que «Descargar validados» baja: el archivo de cada requisito validado,
   * por la IA o por una persona (y la otra cara del DNI, si vino aparte).
   *
   * Lo aprobado en «Otros archivos» NO entra (DEF-109, QA de TEST 20-oct-2026):
   * el botón decía «(2)» con «1 Validado», «Validados (1)» y «1/5 validados»
   * en la misma pantalla. No cumple ningún requisito; se baja desde su tarjeta.
   */
  descargables: Array<{ doc: D; nombre: string }>
}

/** Los motivos del diseño, en su orden. Viajan como `notes` al revisor. */
export const MOTIVOS_DE_RECHAZO = [
  'Ilegible', 'Vencido', 'De otra persona', 'Incompleto', 'Más de 3 meses', 'No es el documento',
] as const

/** El motivo de rechazo que corresponde a lo que observó la IA, o ''. */
export function motivoSugerido(caseId: string | null | undefined): string {
  const c = String(caseId || '')
  if (!c) return ''
  if (c.endsWith('.vencido')) return 'Vencido'
  if (c === 'dni.no_corresponde' || c === 'dni.de_otra_persona') return 'De otra persona'
  if (c.endsWith('.mayor_3_meses')) return 'Más de 3 meses'
  if (c.endsWith('.incompleto') || c.endsWith('.cortado') || c.endsWith('.una_sola_cara')) {
    return 'Incompleto'
  }
  if (c.endsWith('.ilegible') || c === 'calidad.foto' || c.startsWith('intento.')) return 'Ilegible'
  if (c.includes('no_corresponde') || c === 'recepcion.no_identificado') return 'No es el documento'
  return ''
}

/**
 * «17:58» si es de hoy en Lima; «18/10 17:58» si no. `hoy` es `AAAA-MM-DD`
 * de Lima; sin él, el de este momento.
 */
export function cuandoCorto(ts: string | null | undefined, hoy?: string): string {
  const completo = fechaHoraEnLima(ts)
  if (!completo) return ''
  const dia = hoy ?? diaEnLima(new Date().toISOString())
  return diaEnLima(ts) === dia ? completo.slice(6) : completo
}

const RECIBOS = new Set(['RECIBO_LUZ', 'RECIBO_AGUA'])

/**
 * ¿Un archivo de tipo `doc` puede ser de un requisito de tipo `req`? La misma
 * equivalencia que `teleagent.revision.requisitos_del_documento`: un recibo es
 * de luz o de agua, y el HR/PU viejo cubre los dos.
 */
export function mismoTipo(doc: string | null | undefined, req: string | null | undefined): boolean {
  const d = String(doc || '').toUpperCase()
  const r = String(req || '').toUpperCase()
  if (!d || !r || d === 'OTRO') return false
  if (d === r) return true
  if (RECIBOS.has(d) && RECIBOS.has(r)) return true
  return d === 'HR_PU_PREDIAL' && (r === 'HR' || r === 'PU')
}

/** El grupo del filtro de cada estado; `no_aplica` no está en ninguno. */
export function filtroDelEstado(e: EstadoDelRequisito): FiltroDeRequisitos | null {
  if (e === 'rechazado' || e === 'pedido') return 'rechazado'
  if (e === 'no_aplica') return null
  return e
}

function cuandoDecidio(doc: DocConVeredicto & { created_at: string }): string {
  return doc.veredicto_agente?.ts || doc.created_at
}

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`
}

function motivoCorto(doc: DocConVeredicto): string {
  if (doc.status === 'REJECTED') {
    const motivo = (doc.motivo_de_la_persona || '').trim()
    return `Rechazado por ${(doc.revisado_por || '').trim() || 'una persona'}${motivo ? `: ${motivo}` : ''}`
  }
  return (doc.veredicto_agente?.titulo || '').trim() || lecturaDelVeredicto(doc).chip
}

/**
 * El estado del requisito. `ultimo` es el último intento: si la IA observó el
 * requisito y una persona rechazó ese archivo, el requisito está rechazado
 * aunque el checklist no cambie —la conversación solo lo toca cuando se
 * rechaza algo que estaba recibido (`_revision_del_asesor`)—.
 */
function estadoDe(r: RequisitoDelCaso, ultimo: DocDelCaso | null): EstadoDelRequisito {
  if (r.no_aplica) return 'no_aplica'
  if (r.recibido) return 'validado'
  if (r.estado === 'OBSERVADO') {
    const marca = marcaDelRequisito(r)
    if (marca?.tipo === RECHAZADO_POR_ASESOR) return 'rechazado'
    if (marca?.tipo === PEDIDO_DE_NUEVO) return 'pedido'
    return ultimo?.status === 'REJECTED' ? 'rechazado' : 'observado'
  }
  return 'sin_recibir'
}

/**
 * Agrupa los archivos VIGENTES por requisito del checklist. `documentos` son
 * las tarjetas del expediente (sin los rechazados al recibirlos);
 * `requeridos`, los de `progreso`. `pedidoDesde` es cuándo se generó el
 * checklist: la hora de «pedido» de lo que no llegó.
 */
export function agruparPorRequisito<D extends DocDelCaso>(
  documentos: ReadonlyArray<D>,
  requeridos: ReadonlyArray<RequisitoDelCaso> | null | undefined,
  pedidoDesde = '',
  hoy?: string,
): DocumentosPorRequisito<D> {
  const porId = new Map(documentos.map((d) => [d.doc_id, d]))
  const usados = new Set<string>()
  const grupos: Array<GrupoDelRequisito<D>> = []

  ;(requeridos ?? []).forEach((r, i) => {
    const archivo = r.recibido && r.documento_id ? porId.get(r.documento_id) ?? null : null
    const otraCara = archivo && r.documento_otra_cara
      ? porId.get(r.documento_otra_cara) ?? null : null
    // Los que vinieron a cubrir ESTE requisito, por lo que registró el agente.
    // El que la persona rechazó viaja en `documento_id` y también es un intento.
    const intentos = documentos.filter((d) => d !== archivo && d !== otraCara && (
      (d.veredicto_agente?.requisitos ?? []).includes(r.label)
      || (!r.recibido && d.doc_id === r.documento_id)))
      .sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0))
    if (archivo) usados.add(archivo.doc_id)
    if (otraCara) usados.add(otraCara.doc_id)
    intentos.forEach((d) => usados.add(d.doc_id))
    const filas = intentos.map((doc, j) => ({
      doc,
      n: doc.veredicto_agente?.intento || j + 1,
      motivo: motivoCorto(doc),
      hora: cuandoCorto(cuandoDecidio(doc), hoy),
    })).reverse()

    const ultimo = intentos[intentos.length - 1] ?? null
    const estado = estadoDe(r, ultimo)
    const n = r.intentos || intentos.length
    const intentosTexto = estado === 'observado' && n ? plural(n, 'intento', 'intentos') : ''
    const conMensaje = [...intentos].reverse()
      .find((d) => (d.veredicto_agente?.texto_cliente || '').trim())
    const lecturaDelArchivo = archivo ? lecturaDelVeredicto(archivo) : null
    const iaObjeto = estado === 'observado'
      ? ((ultimo?.veredicto_agente?.motivo || '').trim()
         || (ultimo?.veredicto_agente?.titulo || '').trim()
         || (r.observacion || '').trim())
      : ''
    // Sobre qué decide el ejecutivo: el último intento de lo que la IA
    // observó, si nadie lo decidió todavía.
    const aDecidir = estado === 'observado' && ultimo
      && ultimo.status !== 'APPROVED' && ultimo.status !== 'REJECTED' ? ultimo : null
    const quien = (d: D | null) => [(d?.revisado_por || '').trim() || 'una persona',
                                    cuandoCorto(d?.revisado_at, hoy)].filter((x) => x).join(' · ')
    const rechazado = [...intentos].reverse().find((d) => d.status === 'REJECTED') ?? null
    const porPersona = archivo?.status === 'APPROVED'
    const nombre = [otraCara?.filename, archivo?.filename || r.archivo]
      .filter((x) => x).join(' + ')

    let sub: string
    switch (estado) {
      case 'validado':
        if (porPersona) {
          sub = [nombre, `validado por ${quien(archivo)}`].filter((x) => x).join(' · ')
        } else if (archivo) {
          sub = [nombre, lecturaDelArchivo?.observaciones.length
            ? 'validado por la IA, con observaciones' : 'validado por la IA',
                 cuandoCorto(cuandoDecidio(archivo), hoy)].filter((x) => x).join(' · ')
        } else {
          sub = `${nombre || 'Recibido'} · el archivo no quedó registrado`
        }
        break
      case 'observado':
        sub = [ultimo?.filename || '', 'observado por la IA', intentosTexto].filter((x) => x).join(' · ')
        break
      case 'rechazado': {
        const motivo = (rechazado?.motivo_de_la_persona || '').trim()
        sub = [rechazado?.filename || '', `rechazado por ${quien(rechazado)}${motivo ? `: ${motivo}` : ''}`]
          .filter((x) => x).join(' · ')
        break
      }
      case 'pedido':
        sub = ['Pedido de nuevo', cuandoCorto(r.revisado_at, hoy), 'esperando reenvío']
          .filter((x) => x).join(' · ')
        break
      case 'no_aplica':
        // DEF-040: el DNI del titular fallecido no se le pide a nadie.
        sub = `No aplica${r.motivo_no_aplica ? `: ${r.motivo_no_aplica}` : ''}`
        break
      default:
        sub = 'Sin recibir'
    }

    grupos.push({
      clave: `${i}`,
      label: r.label,
      estado,
      requisito: r,
      archivo,
      otraCara,
      validadoPor: estado !== 'validado' ? '' : porPersona ? 'persona' : 'ia',
      aDecidir,
      sub,
      hora: estado === 'validado'
        ? (porPersona ? cuandoCorto(archivo?.revisado_at, hoy)
          : archivo ? cuandoCorto(cuandoDecidio(archivo), hoy) : '')
        : estado === 'rechazado' ? cuandoCorto(rechazado?.revisado_at, hoy)
        : estado === 'pedido' ? cuandoCorto(r.revisado_at, hoy)
        : estado === 'observado' ? (filas[0]?.hora ?? '')
        : estado === 'sin_recibir' ? cuandoCorto(pedidoDesde, hoy) : '',
      observacionIA: iaObjeto,
      observaciones: lecturaDelArchivo?.observaciones ?? [],
      intentosTexto,
      archivos: filas,
      mensaje: conMensaje ? {
        texto: (conMensaje.veredicto_agente?.texto_cliente || '').trim(),
        hora: cuandoCorto(cuandoDecidio(conMensaje), hoy),
      } : null,
      motivoSugerido: motivoSugerido(aDecidir?.veredicto_agente?.case_id || r.ultimo_case_id),
      rechazado: estado === 'rechazado' ? rechazado : null,
      clienteAvisado: estado === 'rechazado' && marcaDelRequisito(r)?.tipo !== RECHAZADO_POR_ASESOR
        && Boolean((rechazado?.veredicto_agente?.texto_cliente || '').trim()),
      candidatos: [],
    })
  })

  const otros = documentos.filter((d) => !usados.has(d.doc_id))
  for (const g of grupos) {
    if (g.estado === 'observado' && g.archivos.length === 0) {
      g.candidatos = otros.filter((d) => !d.otro_inmueble
        && mismoTipo(d.doc_type, g.requisito.doc_type))
    }
  }
  const descargables = grupos.flatMap((g) => (g.estado === 'validado' && g.archivo
    ? [{ doc: g.archivo, nombre: g.label },
       ...(g.otraCara ? [{ doc: g.otraCara, nombre: `${g.label} (otra cara)` }] : [])]
    : []))
  const cuenta = (...e: EstadoDelRequisito[]) => grupos.filter((g) => e.includes(g.estado)).length
  return {
    grupos,
    validados: cuenta('validado'),
    observados: cuenta('observado'),
    rechazados: cuenta('rechazado', 'pedido'),
    sinRecibir: cuenta('sin_recibir'),
    aplicables: grupos.filter((g) => g.estado !== 'no_aplica').length,
    otros,
    descargables,
  }
}

/**
 * Cuántos requisitos del expediente VIGENTE esperan la decisión del ejecutivo:
 * los que la IA observó. Es el número del badge de «Documentos» y del riel del
 * cajón: uno solo, para que no cuenten distinto.
 */
export function cuantosPorDecidir<D extends DocDelCaso & { rechazado_en_ingesta?: boolean }>(
  documentos: ReadonlyArray<D>,
  requeridos: ReadonlyArray<RequisitoDelCaso> | null | undefined,
): number {
  return agruparPorRequisito(separarRechazados([...documentos]).tarjetas, requeridos).observados
}

/**
 * El orden de las secciones de «Documentos» (Diego Casallo, 20-oct-2026: «así
 * el ejecutivo no se confunde»): primero lo que ya está —validado, listo para
 * bajar—, después lo que falta y al final lo que hay que decidir. Lo rechazado
 * va detrás de lo observado, y «Otros archivos» —sin requisito— cierra la
 * lista fuera de este orden.
 */
export const ORDEN_DE_SECCIONES: ReadonlyArray<Exclude<FiltroDeRequisitos, 'todos'>> =
  ['validado', 'sin_recibir', 'observado', 'rechazado']

/** Las secciones que tienen algo, en `ORDEN_DE_SECCIONES`, con sus grupos. */
export function seccionesDeDocumentos<D>(
  grupos: ReadonlyArray<GrupoDelRequisito<D>>,
): Array<{ seccion: Exclude<FiltroDeRequisitos, 'todos'>; grupos: Array<GrupoDelRequisito<D>> }> {
  return ORDEN_DE_SECCIONES
    .map((seccion) => ({ seccion, grupos: grupos.filter((g) => filtroDelEstado(g.estado) === seccion) }))
    .filter((s) => s.grupos.length > 0)
}

/**
 * Qué se pliega en una fila de la Ficha y qué dice plegada (Diego Casallo,
 * 20-oct-2026: «que se pueda expandir o disminuir para que así no se llene de
 * archivos»). Se pliega lo que tiene detalle: lo observado —la observación de
 * la IA, los intentos, los archivos y la decisión— y lo validado con
 * observaciones. Plegada, la fila dice lo que espera adentro: cuántos archivos
 * faltan decidir, o cuántas observaciones hay. Lo demás no tiene nada que
 * desplegar.
 */
export function plegadoDeLaFila<D extends DocDelCaso>(
  g: GrupoDelRequisito<D>,
): { plegable: boolean; resumen: string } {
  if (g.estado === 'observado') {
    const sinDecidir = g.candidatos.filter((c) => c.status !== 'APPROVED' && c.status !== 'REJECTED').length
      + (g.aDecidir ? 1 : 0)
    return {
      plegable: true,
      resumen: sinDecidir ? `${plural(sinDecidir, 'archivo', 'archivos')} por decidir` : 'Ver la observación',
    }
  }
  if (g.estado === 'validado' && g.observaciones.length > 0) {
    return { plegable: true, resumen: plural(g.observaciones.length, 'observación', 'observaciones') }
  }
  return { plegable: false, resumen: '' }
}

/** Los grupos que muestra un filtro. */
export function gruposDelFiltro<D>(
  grupos: ReadonlyArray<GrupoDelRequisito<D>>, filtro: FiltroDeRequisitos,
): Array<GrupoDelRequisito<D>> {
  return grupos.filter((g) => {
    const f = filtroDelEstado(g.estado)
    return f !== null && (filtro === 'todos' || f === filtro)
  })
}
